/**
 * Calendly events for the partner "book a lead intro" flow. These are the
 * round-robin intro events of the Bliks sales team (the same ones the public
 * website links); the n8n intake needs no partner-specific event type, because
 * it attributes a booking to its partner via the rep's HubSpot contact id in
 * the UTM field and via the guest entry (see the automation repo,
 * `docs/betrieb/lead-intake-partnerformular-calendly.md`).
 *
 * Calendly's booking-UI language is set on the EVENT TYPE in Calendly's backend
 * (the `?locale=` URL param does NOT override it — confirmed by Calendly staff),
 * so per-user localization requires one event per language. We map the user's
 * app language to the matching event; anything unmapped (incl. Hungarian, which
 * Calendly doesn't support) falls back to the English event.
 *
 * The event links live in the Bliks Calendly account, not in configuration:
 * every environment books against the same real calendar (there is no Calendly
 * sandbox), and the n8n intake accepts exactly these event types. Calendly
 * resolves a `/d/<id>/<slug>` link by its id and tolerates a stale slug, but a
 * deleted or recreated event type makes the link invalid ("This Calendly URL is
 * not valid" in the embed), so recreating one requires editing this table.
 */
const CALENDLY_EVENTS: Record<string, string> = {
  de: 'https://calendly.com/d/d2fx-54c-696/discovery-call-bliks-io',
  en: 'https://calendly.com/d/ds8s-8gr-bz9/intro-to-bliks-io-en'
};

const CALENDLY_FALLBACK_LANG = 'en';

/**
 * Bliks brand theming for the Calendly widget. The embed API takes literal hex
 * values (no `#`, no CSS custom properties), so the iframe cannot read the app's
 * theme tokens — these are a hand-kept copy of the brand palette and must be
 * updated together with it. `primary_color` is the Bliks brand green, NOT
 * Tailwind's `--color-primary` (which is the UI accent blue).
 */
const CALENDLY_THEME = {
  background_color: 'ffffff',
  text_color: '0a0a0a',
  primary_color: '26ed76'
} as const;

/**
 * Build the partner-lead Calendly URL for the given app language: picks the
 * language-specific event (English fallback), applies the app theme, and hides
 * Calendly's chrome (event name / details) + GDPR banner so the embed is compact
 * and shows nothing untranslated.
 */
export function buildPartnerLeadCalendlyUrl(locale?: string): string {
  const lang = (locale ?? '').split('-')[0]?.toLowerCase() ?? '';
  const base = CALENDLY_EVENTS[lang] ?? CALENDLY_EVENTS[CALENDLY_FALLBACK_LANG];
  const params = new URLSearchParams({
    ...CALENDLY_THEME,
    hide_gdpr_banner: '1',
    // Hide Calendly's own chrome (event name / details) so nothing untranslated
    // shows and the embed stays compact — qualification lives in the Bliks form.
    hide_event_type_details: '1',
    hide_landing_page_details: '1'
  });
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}${params.toString()}`;
}
